/**
 * PEDIR SEGUIMIENTO de un foco: «que alguien vuelva a mirar este sitio».
 *
 * Crea una tarea de MONITOREO normal y corriente —la ve el trabajador entre sus
 * tareas, con su aviso de siempre— pero enlazada al FOCO (`planner_tasks.focus_id`)
 * y NO a un parte. No es un capricho: al completar una tarea, `update-task-status`
 * da por RESUELTO el parte de `scouting_session_id`, y una tarea de «ve a mirar»
 * resolvería justo lo que se quería vigilar. Lo vigila `focoSeguimiento.guard`.
 *
 * Quien la recibe abre la tarea, ve la última foto del sitio y toca «Hacer la
 * revisión»: el asistente de captura se abre con el sitio ya puesto y le pregunta
 * directamente «¿cómo está ahora?».
 *
 * La política de INSERT de `planner_tasks` ya exige admin o encargado CON acceso
 * a la nave: aquí no se repite el control, se enseña el error si lo hay.
 *
 * Hoja inferior de la casa (`FormSheet`, Pablo 27-09-2026): en el móvil era un
 * diálogo centrado; ahora sube desde abajo con el botón fijo al pie.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { FormSheet, FormSheetPrimaryButton, FormSheetSecondaryButton } from "@/components/ui/FormSheet";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { invalidateScoutingData } from "@/lib/scoutingDataInvalidation";
import { invalidateTreatmentData } from "@/lib/treatmentDataInvalidation";
import type { FocusDetail } from "@/hooks/useFocusTimeline";

/** Mañana, en la fecha LOCAL de quien lo pide. */
const tomorrowLocal = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export function RequestFollowUpDialog({ focus, open, onOpenChange }: { focus: FocusDetail | null; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { company, user } = useAuth();
  const queryClient = useQueryClient();
  const [workerId, setWorkerId] = useState("");
  const DEFAULT_TIME = "08:00";
  const [date, setDate] = useState(tomorrowLocal());
  // La HORA prevista (D17): los avisos al trabajador cuelgan de ella («no has empezado» a
  // la hora y media). Por defecto a primera hora de la mañana.
  const [time, setTime] = useState(DEFAULT_TIME);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: workers = [] } = useQuery({
    queryKey: ["follow-up-workers", company?.id],
    enabled: open && !!company?.id,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const [profiles, roles] = await Promise.all([
        supabase.from("profiles").select("user_id, full_name").eq("company_id", company!.id).order("full_name"),
        supabase.from("user_roles").select("user_id, role").eq("company_id", company!.id),
      ]);
      if (profiles.error) throw profiles.error;
      const roleOf = new Map((roles.data ?? []).map((r) => [r.user_id, r.role as string]));
      return (profiles.data ?? [])
        .filter((p) => ["worker", "manager", "perito"].includes(roleOf.get(p.user_id) ?? ""))
        .map((p) => ({ id: p.user_id, name: p.full_name || "—" }));
    },
  });

  /* Por defecto, quien hizo el muestreo más reciente ENTRE LOS QUE SE LES PUEDE
     ASIGNAR una tarea: ya conoce el sitio. Si el último lo hizo un administrador
     —que muestrea pero no recibe tareas— se mira el anterior, no se deja vacío. */
  const lastInspector = useMemo(() => {
    const samplings = focus?.sessions.filter((s) => s.kind === "sampling") ?? [];
    for (let i = samplings.length - 1; i >= 0; i--) {
      const id = samplings[i].inspector_id;
      if (id && workers.some((w) => w.id === id)) return id;
    }
    return null;
  }, [focus, workers]);
  useEffect(() => {
    if (!open) return;
    setDate(tomorrowLocal());
    setTime(DEFAULT_TIME);
    setNote("");
    setWorkerId("");
  }, [open, focus?.id]);
  useEffect(() => {
    if (open && !workerId && lastInspector) setWorkerId(lastInspector);
  }, [open, workerId, lastInspector]);

  const submit = async () => {
    if (!focus || !company?.id || !user?.id || !workerId || !date) return;
    setSaving(true);
    try {
      const line = focus.lineNumber != null ? `Línea: ${focus.lineNumber}` : null; // formato fijo: lo leen el mapa y la ficha
      const { error } = await supabase.from("planner_tasks").insert({
        company_id: company.id,
        title: t("planner.followUp.taskTitle", { pest: focus.pestName }),
        type: "monitoring",
        status: "pending",
        priority: "medium",
        // A primera hora LOCAL del día elegido.
        scheduled_date: new Date(`${date}T${/^\d{2}:\d{2}$/.test(time) ? time : DEFAULT_TIME}:00`).toISOString(),
        greenhouse_id: focus.greenhouseId,
        block_id: focus.blockId,
        assigned_to: workerId,
        created_by: user.id,
        pest_id: focus.pestId,
        pest_name_snapshot: focus.pestName,
        notes: [line, note.trim() || null].filter(Boolean).join("\n") || null,
        // AL FOCO, nunca al parte: completar una tarea resuelve el parte de `scouting_session_id`.
        focus_id: focus.id,
      });
      if (error) throw error;
      toast({ title: t("planner.followUp.created") });
      invalidateScoutingData(queryClient);
      invalidateTreatmentData(queryClient);
      onOpenChange(false);
    } catch (e) {
      console.error(e);
      toast({ title: t("pests.scouting.focusLink.errors.generic"), description: (e as { message?: string })?.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title={t("planner.followUp.title")}
      isDirty={note.trim().length > 0}
      footer={
        <>
          <FormSheetSecondaryButton type="button" onClick={() => onOpenChange(false)} disabled={saving}>{t("common.cancel")}</FormSheetSecondaryButton>
          <FormSheetPrimaryButton type="button" onClick={submit} disabled={saving || !workerId || !date} data-testid="followup-submit">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("planner.followUp.submit")}
          </FormSheetPrimaryButton>
        </>
      }
    >
        <div className="space-y-4 pb-1" data-testid="followup-dialog">
          <div className="space-y-1.5">
            <Label htmlFor="followup-worker">{t("planner.followUp.worker")}</Label>
            <Select value={workerId} onValueChange={setWorkerId}>
              <SelectTrigger id="followup-worker" className="h-11 bg-white" data-testid="followup-worker"><SelectValue placeholder={t("planner.followUp.worker")} /></SelectTrigger>
              <SelectContent>
                {workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="followup-date">{t("planner.followUp.date")}</Label>
            <div className="grid grid-cols-[minmax(0,1fr)_7rem] gap-2">
              <Input id="followup-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-11 min-w-0 bg-white" data-testid="followup-date" />
              <Input id="followup-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className="h-11 min-w-0 bg-white" aria-label={t("planner.followUp.time")} data-testid="followup-time" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="followup-note">{t("planner.followUp.note")}</Label>
            <Textarea id="followup-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="bg-white" data-testid="followup-note" />
          </div>
        </div>
    </FormSheet>
  );
}
